import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Board = number[][];
type Dir = "UP" | "DOWN" | "LEFT" | "RIGHT";

const SIZE = 4;

const emptyBoard = (): Board =>
  Array.from({ length: SIZE }, () => Array(SIZE).fill(0));

const cloneBoard = (b: Board): Board => b.map((r) => [...r]);

const addRandomTile = (b: Board): Board => {
  const empty: Array<[number, number]> = [];
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) if (b[r][c] === 0) empty.push([r, c]);
  if (!empty.length) return b;
  const [r, c] = empty[Math.floor(Math.random() * empty.length)];
  const nb = cloneBoard(b);
  nb[r][c] = Math.random() < 0.9 ? 2 : 4;
  return nb;
};

const initialBoard = (): Board => addRandomTile(addRandomTile(emptyBoard()));

const slideRow = (row: number[]): { row: number[]; gained: number } => {
  const filtered = row.filter((v) => v !== 0);
  let gained = 0;
  for (let i = 0; i < filtered.length - 1; i++) {
    if (filtered[i] === filtered[i + 1]) {
      filtered[i] *= 2;
      gained += filtered[i];
      filtered.splice(i + 1, 1);
    }
  }
  while (filtered.length < SIZE) filtered.push(0);
  return { row: filtered, gained };
};

const move = (b: Board, dir: Dir): { board: Board; gained: number; moved: boolean } => {
  let working = cloneBoard(b);
  let gained = 0;

  // rotate so we always slide LEFT
  const rotateLeft = (m: Board) => {
    const out = emptyBoard();
    for (let r = 0; r < SIZE; r++)
      for (let c = 0; c < SIZE; c++) out[SIZE - 1 - c][r] = m[r][c];
    return out;
  };
  const rotateRight = (m: Board) => {
    const out = emptyBoard();
    for (let r = 0; r < SIZE; r++)
      for (let c = 0; c < SIZE; c++) out[c][SIZE - 1 - r] = m[r][c];
    return out;
  };

  if (dir === "UP") working = rotateLeft(working);
  else if (dir === "DOWN") working = rotateRight(working);
  else if (dir === "RIGHT") working = working.map((r) => [...r].reverse());

  const before = JSON.stringify(working);
  const next: Board = working.map((row) => {
    const { row: nr, gained: g } = slideRow(row);
    gained += g;
    return nr;
  });

  let result = next;
  if (dir === "UP") result = rotateRight(result);
  else if (dir === "DOWN") result = rotateLeft(result);
  else if (dir === "RIGHT") result = result.map((r) => [...r].reverse());

  const moved = JSON.stringify(working) !== before || JSON.stringify(result) !== JSON.stringify(b);
  // simpler moved check:
  const trueMoved = JSON.stringify(result) !== JSON.stringify(b);
  return { board: result, gained, moved: trueMoved };
};

const hasMoves = (b: Board): boolean => {
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      if (b[r][c] === 0) return true;
      if (c + 1 < SIZE && b[r][c] === b[r][c + 1]) return true;
      if (r + 1 < SIZE && b[r][c] === b[r + 1][c]) return true;
    }
  return false;
};

const tileColor = (v: number): string => {
  // Use semantic tokens via opacity/intensity tiers. Higher tile = more vibrant.
  if (v === 0) return "bg-muted/40 text-transparent";
  if (v === 2) return "bg-secondary text-secondary-foreground";
  if (v === 4) return "bg-accent text-accent-foreground";
  if (v === 8) return "bg-primary/40 text-foreground";
  if (v === 16) return "bg-primary/60 text-primary-foreground";
  if (v === 32) return "bg-primary/75 text-primary-foreground";
  if (v === 64) return "bg-primary text-primary-foreground";
  if (v === 128) return "bg-primary text-primary-foreground shadow-glow";
  if (v === 256) return "bg-primary text-primary-foreground shadow-glow";
  if (v === 512) return "bg-primary text-primary-foreground shadow-glow";
  if (v === 1024) return "bg-primary text-primary-foreground shadow-glow ring-2 ring-accent";
  return "bg-primary text-primary-foreground shadow-glow ring-2 ring-accent";
};

interface Props {
  caption?: string;
}

export const Game2048 = ({ caption }: Props) => {
  const [board, setBoard] = useState<Board>(() => initialBoard());
  const [score, setScore] = useState(0);
  const [best, setBest] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    return Number(localStorage.getItem("tryfit_2048_best") ?? 0);
  });
  const [over, setOver] = useState(false);
  const boardRef = useRef(board);
  boardRef.current = board;

  const reset = useCallback(() => {
    setBoard(initialBoard());
    setScore(0);
    setOver(false);
  }, []);

  const apply = useCallback((dir: Dir) => {
    const cur = boardRef.current;
    const { board: next, gained, moved } = move(cur, dir);
    if (!moved) return;
    const withTile = addRandomTile(next);
    setBoard(withTile);
    setScore((s) => {
      const ns = s + gained;
      setBest((b) => {
        const nb = Math.max(b, ns);
        if (typeof window !== "undefined") localStorage.setItem("tryfit_2048_best", String(nb));
        return nb;
      });
      return ns;
    });
    if (!hasMoves(withTile)) setOver(true);
  }, []);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const map: Record<string, Dir> = {
        ArrowUp: "UP", ArrowDown: "DOWN", ArrowLeft: "LEFT", ArrowRight: "RIGHT",
        w: "UP", s: "DOWN", a: "LEFT", d: "RIGHT",
      };
      const next = map[e.key];
      if (next) {
        e.preventDefault();
        apply(next);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [apply]);

  // Touch swipe
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (Math.max(ax, ay) < 24) return;
    if (ax > ay) apply(dx > 0 ? "RIGHT" : "LEFT");
    else apply(dy > 0 ? "DOWN" : "UP");
    touch.current = null;
  };

  const flat = useMemo(() => board.flat(), [board]);

  return (
    <div className="flex w-full flex-col items-center gap-3">
      {caption && (
        <p className="text-center text-sm font-medium text-foreground">{caption}</p>
      )}
      <p className="text-xs text-muted-foreground">Play 2048 while we cook your look ✨</p>

      <div className="flex w-full items-center justify-between text-xs">
        <span className="rounded-full bg-muted px-2.5 py-1 font-semibold">Score: {score}</span>
        <span className="rounded-full bg-muted px-2.5 py-1 font-semibold">Best: {best}</span>
      </div>

      <div
        className="relative w-full max-w-[280px] select-none rounded-xl border border-border bg-muted/40 p-2 shadow-soft"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="grid aspect-square grid-cols-4 gap-1.5">
          {flat.map((v, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center justify-center rounded-md font-bold transition-all duration-150",
                tileColor(v),
                v >= 1024 ? "text-[10px]" : v >= 128 ? "text-xs" : "text-base",
              )}
            >
              {v !== 0 ? v : ""}
            </div>
          ))}
        </div>

        {over && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl bg-background/85 backdrop-blur-sm">
            <p className="text-base font-bold">Game over</p>
            <p className="text-xs text-muted-foreground">Score {score}</p>
            <Button size="sm" onClick={reset} className="mt-1">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Play again
            </Button>
          </div>
        )}
      </div>

      {/* On-screen D-pad */}
      <div className="grid grid-cols-3 gap-1.5">
        <div />
        <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => apply("UP")} aria-label="Up">
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="h-10 w-10" onClick={reset} aria-label="Restart">
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => apply("LEFT")} aria-label="Left">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => apply("DOWN")} aria-label="Down">
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => apply("RIGHT")} aria-label="Right">
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      <p className="text-[11px] text-muted-foreground">Swipe or use arrows / WASD</p>
    </div>
  );
};
